import {AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {NgForm} from '@angular/forms';
import {Subscription} from 'rxjs';
import {AppointmentRequest} from '@shared/models/appointment-request.model';
import {IdName, LoaderService} from 'front-base-angular-core/angular/projects/front-base-angular-core/src/public-api';
import {AppointmentService} from '@shared/services/appointment.service';
import {AppConfigService} from '@shared/services/app-config.service';
import {LoginService} from '@shared/services/login.service';
import {configVariableValues} from '@shared/constants/config-variable-values.constants';
import {SettingsService} from '@shared/services/settings.service';
import {startWith} from 'rxjs/operators';
import {NotificationService} from '@shared/services/notification.service';
import {PermissionService} from '@shared/services/permission.service';

@Component({
  selector: 'si-appo-appointment-request-confirm-date',
  templateUrl: './appo-appointment-request-confirm-date.component.html',
  styleUrls: ['./appo-appointment-request-confirm-date.component.scss']
})
export class AppoAppointmentRequestConfirmDateComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() textError: string;
  @Input() finishError: boolean;
  @Input() model = new AppointmentRequest();
  @Input() form: NgForm;
  @Input() formId = 'appoAppointmentRequestConfirmDateForm';
  @Input() finish: boolean;
  @Input() currentStep: number;
  @Output() finishChange = new EventEmitter<boolean>();
  @Output() formChange = new EventEmitter<NgForm>();
  @Output() textErrorChange = new EventEmitter<string>();
  @Output() finishErrorChange = new EventEmitter<boolean>();
  @Output() slotConfirmationChange = new EventEmitter<number>();
  @Output() slotReservationDeleteChange = new EventEmitter<number>();
  @Output() currentStepChange = new EventEmitter<number>();
  @ViewChild('appoAppointmentRequestConfirmDateForm') appoAppointmentRequestConfirmDateForm: NgForm;
  formSubscription: Subscription;
  maskedEmail: string;
  maskedPhone: string;
  maskMargin = 2;
  showEmailNotification: boolean;
  showSMSNotification: boolean;
  showNotificationWarning: boolean;
  showConfirmAccountWarning: boolean;
  showCollectiveWarning: boolean;
  displaySlotErrorDialog: boolean;
  slotErrorText: string;
  requestAgain: boolean;
  private _slotConfirmation: number;
  private _slotReservationDelete: number;
  private _collectiveWarningCheck: number;

  constructor(
    private loaderService: LoaderService,
    private appointmentService: AppointmentService,
    private appConfigService: AppConfigService,
    private loginService: LoginService,
    private changeDetectorRef: ChangeDetectorRef,
    private settingsService: SettingsService,
    private notificationService: NotificationService,
    private permissionService: PermissionService
  ) {
  }

  get collectiveWarningCheck() {
    return this._collectiveWarningCheck;
  }

  @Input()
  set collectiveWarningCheck(val) {
    this._collectiveWarningCheck = val;
    if (val) {
      this.getCollectiveWarning();
    }
  }

  get slotConfirmation() {
    return this._slotConfirmation;
  }

  @Input()
  set slotConfirmation(val) {
    this._slotConfirmation = val;
    if (val) {
      this.appointmentSlotConfirmation();
    }
  }

  get slotReservationDelete() {
    return this._slotReservationDelete;
  }

  @Input()
  set slotReservationDelete(val) {
    this._slotReservationDelete = val;
    if (val) {
      this.appointmentSlotReservationDelete();
    }
  }

  ngOnInit(): void {
    this.showNotificationWarning = !this.notificationService.arePushNotificationsGranted();
    this.showConfirmAccountWarning = !this.loginService.isLoggedIn();
    this.getSettingsNotification();
    this.getEmailAndPhoneNotification();
    this.getAndMaskEmailAndPhone();
  }

  ngAfterViewInit(): void {
    this.appoAppointmentRequestConfirmDateForm.control.markAsDirty();
    this.changeDetectorRef.detectChanges();
    this.setFormSubscription();
  }

  setFormSubscription(): void {
    this.formSubscription = this.appoAppointmentRequestConfirmDateForm.statusChanges
      .pipe(
        startWith(null) // Emit when subscribing
      ).subscribe(() => {
        this.form = this.appoAppointmentRequestConfirmDateForm;
        this.formChange.emit(this.appoAppointmentRequestConfirmDateForm);
      });
  }

  async getSettingsNotification(): Promise<void> {
    if (!this.permissionService.hasPermission('user_profile:notifications')) {
      return;
    }

    try {
      this.loaderService.start();
      const settingsNotification = await this.settingsService.getSettingsNotification();
      this.model.emailNotification = settingsNotification.emailAppointmentConfirmation;
      this.model.smsNotification = settingsNotification.smsNotification;
    } finally {
      this.loaderService.stop();
    }
  }

  getEmailAndPhoneNotification(): void {
    this.showEmailNotification = this.appConfigService.hasConfigurationEnabled(configVariableValues.EMAIL_APPOINTMENT_NOTIFICATION);
    this.showSMSNotification = this.appConfigService.hasConfigurationEnabled(configVariableValues.SMS_APPOINTMENT_NOTIFICATION);
  }

  getAndMaskEmailAndPhone(): void {
    if (this.showEmailNotification) {
      this.maskedEmail = this.model.email ? this.loginService.getEmailMasked(this.model.email) : this.loginService.getSessionEmailMasked();
    }

    if (this.showSMSNotification) {
      this.maskedPhone = this.model.phone?.value ? this.loginService.getPhoneMasked(this.model.phone?.value) : this.loginService.getSessionPhoneMasked();
    }
  }

  async getCollectiveWarning(): Promise<void> {
    if (this.model.privateInsurance || !this.loginService.isLoggedIn()) {
      this.showCollectiveWarning = false;
      return;
    }

    const collectives = await this.appointmentService.getCollectives(!this.loginService.getIsPatient());
    this.showCollectiveWarning = !collectives?.some(collective => collective.id === this.model.collective?.id);
  }

  async appointmentSlotConfirmation(): Promise<void> {
    await this.getCollectiveWarning();
    try {
      this.loaderService.start();
      let appointmentIdObject: IdName;
      if (this.model.oldAppointmentId) {
        appointmentIdObject = await this.appointmentService.setAppointmentReschedule(this.model);
      } else {
        appointmentIdObject = await this.appointmentService.setAppointmentSlotConfirm(this.model, this.showCollectiveWarning);
      }

      this.model.appointmentId = appointmentIdObject.id;
      setTimeout(() => {
        this.finishChange.emit(true);
      });
    } catch (e) {
      if (e.error?.applicationCode === 'PCS0014') {
        setTimeout(() => {
          this.textErrorChange.emit(e.error?.message);
          this.finishErrorChange.emit(true);
        });
      }

      if (e.error?.applicationCode === 'PCS0059') {
        this.slotErrorText = e.error?.message;
        this.displaySlotErrorDialog = true;
      }

      throw e;
    } finally {
      this.slotConfirmationChange.emit(0);
      this.loaderService.stop();
    }
  }

  onClickRequestAgainButton(): void {
    this.requestAgain = true;
    this.appointmentSlotReservationDelete();
  }

  async appointmentSlotReservationDelete(): Promise<void> {
    try {
      // Avoid deleting when there is no reservation
      if (this.model.reserveId) {
        await this.appointmentService.deleteAppointmentSlotReserve(this.model.reserveId);
      }
    } finally {
      setTimeout(() => {
        // The 'Request Again' popup must take you back to the first step. Otherwise, back to the second step
        if (this.requestAgain) {
          this.currentStepChange.emit(0);
        } else {
          this.currentStepChange.emit(1);
        }
      });
      this.model.reserveId = null;
      this.slotReservationDeleteChange.emit(0);
    }
  }

  ngOnDestroy(): void {
    this.formSubscription.unsubscribe();
  }
}
